<template>
  <Select
    unstyled
    :pt="theme"
    :pt-options="{
      mergeProps: ptViewMerge,
    }"
  >
    <template #dropdownicon>
      <i class="pi pi-code rotate-90 text-surface-400" />
    </template>
    <template #loadingicon>
      <SpinnerIcon class="animate-spin" />
    </template>
    <template #filtericon>
      <SearchIcon class="text-surface-400" />
    </template>
    <template #clearicon="{ clearCallback }">
      <TimesIcon
        class="text-surface-400 absolute top-1/2 -mt-2 end-10"
        @click="clearCallback"
      />
    </template>
    <template v-for="(_, slotName) in $slots" #[getTypedSlotName(slotName)]="slotProps">
      <slot :name="slotName" v-bind="slotProps ?? {}" />
    </template>
  </Select>
</template>

<script setup lang="ts">
import SearchIcon from "@primevue/icons/search";
import SpinnerIcon from "@primevue/icons/spinner";
import TimesIcon from "@primevue/icons/times";
import Select, {
  type SelectPassThroughOptions,
  type SelectProps,
  type SelectSlots,
} from "primevue/select";
import { ref } from "vue";
import { createSlotPassThroughGetter, ptViewMerge } from "./utils";

const getTypedSlotName = createSlotPassThroughGetter<SelectSlots>();

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface Props extends /* @vue-ignore */ SelectProps {}
defineProps<Props>();

const theme = ref<SelectPassThroughOptions>({
  root: `inline-flex cursor-pointer relative select-none rounded-xl p-fluid:flex
        ma-neutral-card ma-shadow
        text-surface-700 dark:text-surface-0
        p-focus:border-surface-100 dark:p-focus:border-surface-700
        enabled:hover:border-surface-100 dark:enabled:hover:border-surface-700
        p-invalid:border-red-100 dark:p-invalid:border-red-100
        p-disabled:opacity-60 p-disabled:pointer-events-none
        transition-colors duration-200 w-full`,
  label: `block whitespace-nowrap overflow-hidden flex-auto w-[1%]
        py-2 px-3 overflow-ellipsis
        p-clearable:pe-7 p-empty:overflow-hidden p-empty:opacity-0 p-editable:cursor-default
        text-surface-700 dark:text-surface-0 bg-transparent border-none outline-none
        p-placeholder:text-surface-400 dark:p-placeholder:text-surface-500 p-placeholder:font-medium
        p-disabled:text-surface-500 dark:p-disabled:text-surface-400
        p-small:text-sm p-small:px-[0.625rem] p-small:py-[0.375rem]
        p-large:text-lg p-large:px-[0.875rem] p-large:py-[0.625rem]`,
  dropdown: `flex items-center justify-center shrink-0 bg-transparent
        text-surface-400 w-10 rounded-e-xl`,
  overlay: `absolute top-0 left-0 z-[1800]! rounded-xl p-portal-self:min-w-full
        bg-surface-0 dark:bg-surface-800
        border border-surface-200 dark:border-surface-700
        text-surface-700 dark:text-surface-0
        shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-2px_rgba(0,0,0,0.1)]`,
  header: `pt-2 pb-1 px-4`,
  pcFilterContainer: {
    root: `relative`,
  },
  pcFilter: {
    root: `w-full appearance-none rounded-lg outline-hidden
            ma-neutral-card ma-inner-shadow
            text-surface-700 dark:text-surface-0
            placeholder:text-surface-400 dark:placeholder:text-surface-500 placeholder:font-medium
            enabled:focus:border-surface-100
            disabled:opacity-60
            ps-3 pe-10 py-2 p-fluid:w-full
            transition-colors duration-200`,
  },
  pcFilterIconContainer: {
    root: `absolute top-1/2 -mt-2 leading-none end-3 z-1`,
  },
  listContainer: `overflow-auto`,
  list: `m-0 p-1 list-none gap-[2px] flex flex-col`,
  optionGroup: `m-0 px-3 py-2 bg-transparent text-surface-500 dark:text-surface-400 font-semibold`,
  optionGroupLabel: ``,
  option: `cursor-pointer font-normal whitespace-nowrap relative overflow-hidden flex items-center
        px-3 py-2 border-none text-surface-700 dark:text-surface-0 bg-transparent rounded-lg
        p-focus:bg-surface-100 dark:p-focus:bg-surface-700/40 p-focus:text-surface-800 dark:p-focus:text-surface-0
        p-selected:bg-highlight p-focus:p-selected:bg-highlight-emphasis
        transition-colors duration-200`,
  optionLabel: ``,
  optionCheckIcon: `relative -ms-[0.375rem] me-[0.375rem] text-surface-700 dark:text-surface-0`,
  optionBlankIcon: ``,
  emptyMessage: `px-3 py-2`,
  virtualScroller: ``,
  transition: {
    enterFromClass: "opacity-0 scale-y-75",
    enterActiveClass: "transition duration-120 ease-[cubic-bezier(0,0,0.2,1)]",
    leaveActiveClass: "transition-opacity duration-100 ease-linear",
    leaveToClass: "opacity-0",
  },
});
</script>
